import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { BedarfStatus, BedarfStatusConfig, Bedarf } from '../../types/bedarf';
import { InfoTooltip } from '../common/InfoTooltip';
import { KanbanCard } from './KanbanCard';

interface KanbanColumnProps {
  status: BedarfStatus;
  bedarfe: Bedarf[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function KanbanColumn({ status, bedarfe, isCollapsed, onToggleCollapse }: KanbanColumnProps) {
  const config = BedarfStatusConfig[status];
  const { setNodeRef, isOver } = useDroppable({
    id: status,
    data: {
      type: 'column',
      status,
    },
  });

  const bedarfIds = bedarfe.map((b) => b.id);

  // Collapsed view - narrow vertical strip
  if (isCollapsed) {
    return (
      <div
        ref={setNodeRef}
        data-testid={`spalte-${status}`}
        onClick={onToggleCollapse}
        title={`${config.label} — ${config.description}`}
        className={`
          w-10 flex-shrink-0 bg-gray-50 rounded-lg cursor-pointer
          hover:bg-gray-100 flex flex-col items-center py-3
          transition-all duration-200
          min-h-[200px] max-h-[calc(100vh-280px)] lg:max-h-full
          ${isOver ? 'bg-primary-50 ring-2 ring-primary-300' : ''}
        `}
      >
        {/* Count badge at top */}
        <span className={`inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
          {bedarfe.length}
        </span>

        {/* Rotated label - top aligned, horizontally centered */}
        <div className="mt-3">
          <span
            className={`text-sm font-medium ${config.color} whitespace-nowrap`}
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            {config.label}
          </span>
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div
      data-testid={`spalte-${status}`}
      className="flex flex-col bg-gray-50 rounded-lg min-w-[220px] max-w-[280px] flex-shrink-0"
    >
      {/* Column Header - clickable to collapse */}
      <div
        className={`px-3 py-2 rounded-t-lg ${config.bgColor} cursor-pointer hover:opacity-90 transition-opacity`}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <svg className={`h-4 w-4 ${config.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <h3 className={`text-sm font-semibold ${config.color}`}>
              {config.label}
            </h3>
            <InfoTooltip
              text={config.description}
              label={`Erklärung: ${config.label}`}
              className={config.color}
            />
          </div>
          <span className={`inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
            {bedarfe.length}
          </span>
        </div>
      </div>

      {/* Column Content */}
      <div
        ref={setNodeRef}
        className={`
          flex-1 p-2 overflow-y-auto min-h-[200px] max-h-[calc(100vh-280px)] lg:max-h-full
          transition-colors duration-200
          ${isOver ? 'bg-primary-50 ring-2 ring-primary-300 ring-inset' : ''}
        `}
      >
        <SortableContext items={bedarfIds} strategy={verticalListSortingStrategy}>
          {bedarfe.length > 0 ? (
            bedarfe.map((bedarf) => (
              <KanbanCard key={bedarf.id} bedarf={bedarf} />
            ))
          ) : (
            <div className="flex items-center justify-center h-20 text-gray-400 text-sm">
              <div className="text-center">
                <svg className="mx-auto h-8 w-8 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p>Keine Bedarfe</p>
              </div>
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
}
